'use client'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

const DURATION_MODERATE = 0.4
// The blur may use at most this share of a phrase's slot, so a short step still
// leaves the phrase settled and readable for most of it.
const MAX_TRANSITION_SHARE = 0.3
const EASING_CUSTOM_OUT = [0.165, 0.84, 0.44, 1] as const

type BlurRevealPhrasesProps = {
  className?: string
  intervalMs?: number
  onComplete?: () => void
  phrases: string[]
  // Render the first phrase already entered, e.g. when it was on screen in the
  // server HTML before hydration.
  skipEntrance?: boolean
  // Epoch ms the schedule counts from; defaults to mount. A start in the past
  // catches up to the phrase that is due now.
  startedAt?: number
}

// Phrase `i` enters at `i * intervalMs` and crossfades with the one leaving, so
// every phrase gets the same slot rather than waiting out the previous exit.
export function BlurRevealPhrases({
  phrases,
  className,
  intervalMs = 1600,
  onComplete,
  skipEntrance = false,
  startedAt,
}: BlurRevealPhrasesProps) {
  const [index, setIndex] = useState(0)
  const [mountedAt] = useState(() => Date.now())
  const start = startedAt ?? mountedAt
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const isLast = index >= phrases.length - 1
  const duration = Math.min(
    DURATION_MODERATE,
    (intervalMs * MAX_TRANSITION_SHARE) / 1000,
  )

  useEffect(() => {
    if (isLast) {
      const timer = setTimeout(() => onCompleteRef.current?.(), duration * 1000)
      return () => clearTimeout(timer)
    }

    const due = start + (index + 1) * intervalMs - Date.now()
    const timer = setTimeout(() => setIndex(index + 1), Math.max(0, due))
    return () => clearTimeout(timer)
  }, [index, isLast, intervalMs, duration, start])

  return (
    <div className="grid justify-items-center">
      <AnimatePresence initial={!skipEntrance}>
        <motion.p
          animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
          className={`col-start-1 row-start-1 font-medium text-base${
            className ? ` ${className}` : ''
          }`}
          exit={{ opacity: 0, filter: 'blur(4px)', y: -12 }}
          initial={{ opacity: 0, filter: 'blur(4px)', y: 12 }}
          key={index}
          transition={{ duration, ease: EASING_CUSTOM_OUT }}
        >
          {phrases[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  )
}
